/**
 * CheckInConversation - Voice-first AI-driven patient intake during SecureLink check-in
 * 
 * Uses VoiceConversationBackendSwitch (Classic pipeline or Gemini Live) for voice;
 * legacy VoiceConversationEngine remains the default Classic implementation.
 * pause detection, UI). The LLM-driven question logic is provided via onPauseDetected.
 * 
 * Flow:
 * 1. Engine speaks the opening message
 * 2. Patient responds via voice (or text fallback)
 * 3. On pause detection, engine calls our handlePauseDetected
 * 4. We send conversation history + goals to the LLM (securelink-conversation query)
 * 5. LLM returns next question or completion with findings
 * 6. Engine speaks the next question
 * 7. Repeat until LLM says conversation is complete
 * 
 * On completion, structured conversationResults are returned to the parent.
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  ChatOutlined,
  CheckCircleOutlined,
} from '@mui/icons-material';

import { VoiceConversationBackendSwitch } from 'components/voice';
import { llmProviderService } from '../../services/llm/llmProviderService';
import { getFirebaseHttpsCallableUserMessage } from '../../utils/firebaseHttpsCallableUserMessage';

/** Shown when a new pause arrives while the prior LLM request is still running. */
const LLM_BUSY_USER_HINT =
  'Hang on — we are still processing your last answer. Please wait a moment before speaking again.';

/**
 * @param {Object} props
 * @param {Object} props.conversationTemplate - Resolved template { title, goals, maxQuestions }
 * @param {Object} [props.patientCMR] - Patient CMR data for context
 * @param {string} [props.medicationsReview] - 'none' | 'medications' | 'full_cmr'; when not 'none', intake must not ask about those topics
 * @param {function} props.onComplete - Called with conversationResults when done
 * @param {function} props.onSkip - Called if patient skips
 * @param {string[]} [props.endActions] - Actions to run after completion
 * @param {string[]} [props.suggestedQuestions] - Seed questions for the AI to prioritize
 */
const CheckInConversation = ({
  conversationTemplate,
  patientCMR,
  medicationsReview = 'none',
  onComplete,
  onSkip,
  endActions = [],
  suggestedQuestions = [],
}) => {
  const [isComplete, setIsComplete] = useState(false);
  const [finalResults, setFinalResults] = useState(null);
  const [error, setError] = useState(null);
  const [isWrappingUp, setIsWrappingUp] = useState(false);
  const [llmBusy, setLlmBusy] = useState(false);
  const [continueSubmitted, setContinueSubmitted] = useState(false);
  const findingsSoFarRef = useRef([]);
  const llmInFlightRef = useRef(false);
  const wrapUpInFlightRef = useRef(false);

  const maxQuestions = conversationTemplate?.maxQuestions || 10;
  const title = conversationTemplate?.title || conversationTemplate?.name || 'Health Intake';

  // =========================================================================
  // CMR CONTEXT FORMATTING
  // =========================================================================

  const formatCMRContext = useCallback(() => {
    if (!patientCMR) return 'No patient CMR data available.';
    const parts = [];
    const demo = patientCMR.demographics;
    if (demo) {
      parts.push(`Patient: ${demo.firstName || ''} ${demo.lastName || ''}, DOB: ${demo.dateOfBirth || 'Unknown'}, Sex: ${demo.sex || 'Unknown'}`);
    }
    if (patientCMR.medications?.length > 0) {
      parts.push(`Medications: ${patientCMR.medications.map(m => m.name || m.medicationName).join(', ')}`);
    }
    if (patientCMR.allergies?.length > 0) {
      parts.push(`Allergies: ${patientCMR.allergies.map(a => a.allergen).join(', ')}`);
    }
    if (patientCMR.pmh?.length > 0) {
      parts.push(`PMH: ${patientCMR.pmh.map(c => c.condition).join(', ')}`);
    }
    return parts.length > 0 ? parts.join('\n') : 'No patient CMR data available.';
  }, [patientCMR]);

  // =========================================================================
  // LLM CALL
  // =========================================================================

  const buildUserInput = useCallback((conversationLog, options = {}) => {
    const historyText = conversationLog.map(entry =>
      `${entry.speaker === 'assistant' ? 'Assistant' : 'Patient'}: ${entry.text}`
    ).join('\n');

    // Count how many questions the assistant has already asked
    const questionsAskedSoFar = conversationLog.filter(e => e.speaker === 'assistant').length;

    let urgency = '';
    if (options.wrapUp) {
      urgency = '\n\n*** THE PATIENT HAS ENDED THE CONVERSATION. Based on the entire conversation above, you MUST now set isComplete to true and provide your final findings (structured by topic/system as per the goals), summary, and closingMessage. Do NOT ask another question. ***';
    } else if (questionsAskedSoFar >= maxQuestions) {
      urgency = '\n\n*** YOU HAVE REACHED THE MAXIMUM QUESTION LIMIT. You MUST set isComplete to true NOW and provide your closingMessage, findings, and summary. Do NOT ask another question. ***';
    } else if (questionsAskedSoFar >= maxQuestions - 1) {
      urgency = '\n\n*** This is your LAST question. After the patient responds, you MUST complete the conversation on the next turn. ***';
    }

    // MULTI-FACILITY TODO: medicationsReview is a per-template setting that applies uniformly
    // regardless of delivery channel (in-clinic vs targeted remote). When multi-facility support
    // is added, consider whether different facilities should override this per-clinic for the same template.
    let skipInstruction = '';
    if (medicationsReview === 'medications') {
      skipInstruction = 'IMPORTANT: Do not ask about current medications, medication changes, or adherence; a separate medications review step will follow. Focus on other intake goals only.\n\n';
    } else if (medicationsReview === 'full_cmr') {
      skipInstruction = 'IMPORTANT: Do not ask about medications, allergies, past medical history, surgical history, or other CMR history; a separate full CMR review step will follow. Focus on chief complaint, reason for visit, and other intake goals only.\n\n';
    }

    const suggestedQuestionsAppendix = suggestedQuestions?.length > 0
      ? `\n\nSuggested questions to consider covering (adapt as appropriate):\n${suggestedQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n')}`
      : '';

    return `${skipInstruction}CONVERSATION_GOALS: ${conversationTemplate.goals}

PATIENT_CMR_CONTEXT: ${formatCMRContext()}

MAX_QUESTIONS: ${maxQuestions}
QUESTIONS_ASKED_SO_FAR: ${questionsAskedSoFar}

CONVERSATION_HISTORY:
${historyText || '(No history yet - ask the first question)'}${urgency}${suggestedQuestionsAppendix}`;
  }, [conversationTemplate, formatCMRContext, maxQuestions, medicationsReview, suggestedQuestions]);

  const callLLM = useCallback(async (conversationLog, options = {}) => {
    const userInput = buildUserInput(conversationLog, options);
    const response = await llmProviderService.callLLMWithQuery(
      'securelink-conversation',
      userInput,
      {
        module: 'securelink',
        featureName: 'intake-conversation',
      }
    );

    let output = response.output;

    // Parse string output to object
    if (typeof output === 'string') {
      // Strip markdown code fences the LLM sometimes wraps around JSON
      const stripped = output.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
      try {
        output = JSON.parse(stripped);
      } catch {
        // Unparseable — treat as a plain conversational reply
        return { isComplete: false, nextQuestion: output.trim(), findingsSoFar: [] };
      }
    }

    // The Query Manager orchestrator may inject source-tagging instructions that cause
    // the LLM to return { response: "...", source: "global" } instead of the expected
    // { isComplete, nextQuestion, findingsSoFar } structure. Detect and unwrap.
    if (output && typeof output.response === 'string' && output.nextQuestion === undefined) {
      const inner = output.response;
      // Try parsing the inner response as JSON (it might itself be the structured object)
      try {
        const parsed = JSON.parse(inner);
        if (typeof parsed === 'object' && parsed !== null) {
          output = parsed;
        }
      } catch {
        // Inner response is plain text — treat as the next question
        return { isComplete: false, nextQuestion: inner, findingsSoFar: [] };
      }
    }

    return output;
  }, [buildUserInput]);

  // =========================================================================
  // PAUSE HANDLER (the "brain" for the voice engine)
  // =========================================================================

  const handlePauseDetected = useCallback(async ({ conversationLog }) => {
    if (llmInFlightRef.current) {
      console.warn('[CheckInConversation] Pause ignored: LLM request already in flight');
      setError(LLM_BUSY_USER_HINT);
      return null;
    }

    llmInFlightRef.current = true;
    setLlmBusy(true);
    setError(null);

    // Count assistant turns to know how many questions have been asked
    const questionsAskedSoFar = conversationLog.filter(e => e.speaker === 'assistant').length;

    try {
      const result = await callLLM(conversationLog);

      if (result.findingsSoFar) {
        findingsSoFarRef.current = result.findingsSoFar;
      }

      // LLM signalled completion
      if (result.isComplete) {
        const results = {
          title,
          findings: result.findings || result.findingsSoFar || findingsSoFarRef.current,
          summary: result.summary || '',
          transcript: conversationLog,
          completedAt: new Date().toISOString(),
        };
        setFinalResults(results);
        setIsComplete(true);
        return {
          question: result.closingMessage || 'Thank you for sharing that information! Your provider will review everything before your appointment.',
          isComplete: true,
        };
      }

      // Client-side hard cap: if we've already asked maxQuestions, force wrap-up
      if (questionsAskedSoFar >= maxQuestions) {
        const results = {
          title,
          findings: result.findingsSoFar || findingsSoFarRef.current,
          summary: result.summary || 'Conversation completed — maximum questions reached.',
          transcript: conversationLog,
          completedAt: new Date().toISOString(),
        };
        setFinalResults(results);
        setIsComplete(true);
        return {
          question: 'Thank you so much for answering those questions! Your provider will have all of this information ready for your visit.',
          isComplete: true,
        };
      }

      if (result.nextQuestion) {
        return { question: result.nextQuestion, isComplete: false };
      }

      return null;
    } catch (err) {
      console.error('[CheckInConversation] LLM error:', err);
      const { userMessage } = getFirebaseHttpsCallableUserMessage(err);
      setError(userMessage);
      return null;
    } finally {
      llmInFlightRef.current = false;
      setLlmBusy(false);
    }
  }, [callLLM, title, maxQuestions]);

  // =========================================================================
  // COMPLETION HANDLERS
  // =========================================================================

  async function executeEndActions(actions, results) {
    if (!actions || actions.length === 0) return;

    for (const action of actions) {
      try {
        if (action === 'save_to_cmr') {
          // TODO: implement save_to_cmr
          // Call seedCMRFromConversation(patientId, results) when available
          console.log('[endAction] save_to_cmr — not yet implemented');
        } else if (action === 'notify_provider') {
          // TODO: implement notify_provider
          // Call notifyProvider(dispatchedBy, results) when available
          console.log('[endAction] notify_provider — not yet implemented');
        } else if (action === 'show_facility_map') {
          // TODO: implement show_facility_map
          // Navigate to /map with triage context when available
          console.log('[endAction] show_facility_map — not yet implemented');
        } else if (action === 'route_to_checkin') {
          // TODO: implement route_to_checkin
          // Use createCheckInVisit pattern when available
          console.log('[endAction] route_to_checkin — not yet implemented');
        }
      } catch (err) {
        console.error('[endAction] failed for action:', action, err);
      }
    }
  }

  const submitFinalResults = useCallback(
    (results) => {
      onComplete(results);
      executeEndActions(endActions, results);
    },
    [endActions, onComplete]
  );

  const handleEngineComplete = useCallback((log) => {
    if (finalResults) {
      submitFinalResults(finalResults);
      return;
    }
    if (wrapUpInFlightRef.current) {
      return;
    }
    wrapUpInFlightRef.current = true;
    // User ended the conversation early — run one final LLM pass on full transcript
    // so findings/summary (and thus ROS extraction) get full data
    setIsWrappingUp(true);
    (async () => {
      try {
        const result = await callLLM(log || [], { wrapUp: true });
        const findings = result?.findings || result?.findingsSoFar || findingsSoFarRef.current || [];
        const summary = result?.summary || 'Conversation ended early by user.';
        const earlyResults = {
          title,
          findings: Array.isArray(findings) ? findings : [],
          summary: typeof summary === 'string' ? summary : 'Conversation ended early by user.',
          transcript: log || [],
          completedAt: new Date().toISOString(),
          endedEarly: true,
        };
        setFinalResults(earlyResults);
        setIsComplete(true);
      } catch (err) {
        console.error('[CheckInConversation] Wrap-up LLM error:', err);
        const earlyResults = {
          title,
          findings: findingsSoFarRef.current || [],
          summary: 'Conversation ended early by user.',
          transcript: log || [],
          completedAt: new Date().toISOString(),
          endedEarly: true,
        };
        setFinalResults(earlyResults);
        setIsComplete(true);
      } finally {
        setIsWrappingUp(false);
      }
    })();
  }, [finalResults, submitFinalResults, title, callLLM]);

  const handleSubmitResults = () => {
    if (!finalResults || continueSubmitted) {
      return;
    }
    setContinueSubmitted(true);
    submitFinalResults(finalResults);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  if (isWrappingUp) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress size={32} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Preparing your summary…
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Organizing what you shared for your provider to review. They will still need to confirm details with you.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (isComplete && finalResults) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Chip
            icon={<CheckCircleOutlined />}
            label="Conversation Complete"
            color="success"
            sx={{ mb: 2 }}
          />
          <Typography variant="body1" sx={{ mb: 1 }}>
            {finalResults.summary || 'Thank you for providing your information.'}
          </Typography>
          {finalResults.findings?.length > 0 && (
            <Box sx={{ textAlign: 'left', mb: 2, px: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Information collected:
              </Typography>
              {finalResults.findings.map((finding, idx) => (
                <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                  {finding}
                </Typography>
              ))}
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitResults}
            disabled={continueSubmitted}
            startIcon={<CheckCircleOutlined />}
            size="large"
            sx={{ mt: 1 }}
          >
            Continue Check-In
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ChatOutlined color="info" />
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Please answer a few questions to help your care team prepare for your visit. You can speak naturally.
          This conversation uses automated tools; your answers are not a medical diagnosis and do not replace
          in-person evaluation or emergency care if you need it.
        </Typography>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {llmBusy && (
        <LinearProgress
          sx={{ mb: 2, borderRadius: 1 }}
          aria-label="Processing your answer"
        />
      )}

      {/* Voice Conversation Engine */}
      <VoiceConversationBackendSwitch
        title={title}
        onPauseDetected={handlePauseDetected}
        openingMessage={`Hi! I'd like to ask you a few questions about ${conversationTemplate?.title?.toLowerCase() || 'your health'} to help your provider prepare for your visit. Let's get started. ${conversationTemplate?.goals ? '' : ''}`}
        maxQuestions={maxQuestions}
        compact={false}
        showLanguageSelector={true}
        onComplete={handleEngineComplete}
        onSkip={handleSkip}
        autoStart={true}
        askFirstQuestionAfterOpening={true}
        context={{
          conversationTemplate,
          patientCMR,
        }}
      />
    </Box>
  );
};

export default CheckInConversation;
